import { apiUrl } from '../utils/api-url';

/**
 * Summary row returned by `GET /api/customers` — derived from project counts and
 * augmented with the customer doc's display name when a CustomerDocument exists.
 * Backward-compatible with the original {id, projectCount} shape from #143.
 */
export interface CustomerSummary {
  id: string;
  projectCount: number;
  displayName: string;
  legalName?: string;
  isPublicSector?: boolean;
}

export interface BrandingInfo {
  logoBase64?: string;
  logoUrl?: string;
  primaryColour?: string;
  secondaryColour?: string;
  accentColour?: string;
  textColour?: string;
}

/** Full Cosmos `customers` container record. */
export interface CustomerDocument {
  id: string;
  legalName: string;
  displayName: string;
  country?: string;
  sector?: string;
  isPublicSector: boolean;
  regulatoryProfile?: string;
  address?: string;
  branding: BrandingInfo;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
  /**
   * This customer's alias in the Ops Agent tenant registry — the key the nightly
   * programme-snapshot push writes under (#503). Leave unset wherever it matches
   * the slug (the common case); set it where they differ, because Ops aliases are
   * short opaque handles (`metair` for Metair Investments, `or` for OR Tambo) while
   * the slug is a readable identifier that appears in URLs.
   */
  opsAlias?: string;
  /**
   * Join-key check returned by PUT /api/customers/{id} (#435 Lane E) — additive,
   * never persisted, and since #503 computed against the RESOLVED alias rather than
   * the raw slug. A customer whose alias Ops does not know silently receives no
   * snapshots, for ever. `unknown` means exactly that and must be shown to the user:
   * the API has always returned it, and the UI used to discard it, which is how
   * a mismatched demo slug went unnoticed until a weekly report came back empty.
   */
  opsAliasStatus?: 'verified' | 'unknown' | 'unchecked';
}

export interface CustomerPatch {
  legalName?: string;
  displayName?: string;
  country?: string;
  sector?: string;
  isPublicSector?: boolean;
  regulatoryProfile?: string;
  address?: string;
  branding?: Partial<BrandingInfo>;
  /** Ops registry alias (#503). Empty string clears it back to the slug. */
  opsAlias?: string;
}

/** Minimal create body for PUT /api/customers/{id} — name fields only. */
export interface CustomerCreate {
  legalName: string;
  displayName: string;
}

export interface ImportResult {
  customer: CustomerDocument;
  patchedProjectId?: string;
  overrideKeysSet: string[];
  logoEmbedded: boolean;
}

export async function listCustomers(token: string): Promise<CustomerSummary[]> {
  const res = await fetch(apiUrl('/api/customers'), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to list customers: ${res.status}`);
  return res.json();
}

export async function getCustomer(id: string, token: string): Promise<CustomerDocument | null> {
  const res = await fetch(apiUrl(`/api/customers/${encodeURIComponent(id)}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to get customer ${id}: ${res.status}`);
  return res.json();
}

/**
 * Create-or-update a customer record by slug (epic #400). Minimal by design — only the
 * name fields; branding, sector and regulatory profile stay PATCH-editable afterwards.
 */
export async function createCustomer(
  id: string,
  body: CustomerCreate,
  token: string,
): Promise<CustomerDocument> {
  const res = await fetch(apiUrl(`/api/customers/${encodeURIComponent(id)}`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to create customer ${id}: ${res.status}`);
  return res.json();
}

export async function patchCustomer(
  id: string,
  patch: CustomerPatch,
  token: string,
): Promise<CustomerDocument> {
  const res = await fetch(apiUrl(`/api/customers/${encodeURIComponent(id)}`), {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to patch customer ${id}: ${res.status}`);
  return res.json();
}

/**
 * Trigger the admin importer. Reads `projects/<customerId>/variables.json` from the
 * backend's working tree, upserts the CustomerDocument with branding, and patches the
 * active project's customerOverrides with the dotted-key flattened view.
 */
export async function importCustomer(customerId: string, token: string): Promise<ImportResult> {
  const res = await fetch(
    apiUrl(`/api/admin/import-customer/${encodeURIComponent(customerId)}`),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Failed to import customer ${customerId}: ${res.status} — ${body}`);
  }
  return res.json();
}
